import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import {
  ArrowLeft,
  CreditCard,
  Users,
  TrendingUp,
  DollarSign,
  Clock,
  Crown,
  Zap,
  Medal,
  User
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_3e64acab-2561-42e6-b860-06b1a563df4d/artifacts/5nvw3yv2_6ECA6BBA-F429-4238-A743-EE6BF2D1D33F.png";

const TIER_ICONS = {
  starter: <Zap className="h-5 w-5 text-blue-400" />,
  basic: <Medal className="h-5 w-5 text-green-400" />,
  pro: <Crown className="h-5 w-5 text-yellow-400" />,
  elite: <Crown className="h-5 w-5 text-purple-400" />
};

const TIER_COLORS = {
  starter: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  basic: "bg-green-500/20 text-green-400 border-green-500/30",
  pro: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  elite: "bg-purple-500/20 text-purple-400 border-purple-500/30"
};

export default function AdminSubscriptions() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin");
      return;
    }
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/admin/subscriptions`);
      setData(response.data);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate("/admin");
      }
      toast.error("Failed to load subscription data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary animate-pulse text-2xl font-bold">LOADING...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/admin/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-5 w-5" /> Dashboard
            </Link>
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Coach Coop" className="h-8 w-8 rounded-sm" />
              <span className="font-bold tracking-tight">SUBSCRIPTIONS</span>
            </div>
            <div className="w-24" />
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Revenue Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-green-500/20 to-green-500/5 border-green-500/30">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-500/20 rounded-full">
                  <DollarSign className="h-6 w-6 text-green-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Est. Monthly Revenue</p>
                  <p className="text-3xl font-black text-green-400">
                    ${data?.revenue_estimate?.total_monthly_equivalent?.toLocaleString() || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-primary/20 to-primary/5 border-primary/30">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-primary/20 rounded-full">
                  <CreditCard className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Subscribed</p>
                  <p className="text-3xl font-black text-primary">
                    {data?.total_subscribed || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-500/20 to-orange-500/5 border-orange-500/30">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-orange-500/20 rounded-full">
                  <Clock className="h-6 w-6 text-orange-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Expired Trials</p>
                  <p className="text-3xl font-black text-orange-400">
                    {data?.expired_trial_users || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Subscription Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-primary" />
                Subscription Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(data?.subscription_breakdown || {}).map(([tier, count]) => (
                  <div key={tier} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${TIER_COLORS[tier]?.split(' ')[0]}`}>
                        {TIER_ICONS[tier]}
                      </div>
                      <span className="font-bold uppercase">{tier}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-32 bg-secondary rounded-full h-2 overflow-hidden">
                        <div 
                          className={`h-full ${tier === 'elite' ? 'bg-purple-500' : tier === 'pro' ? 'bg-yellow-500' : tier === 'basic' ? 'bg-green-500' : 'bg-blue-500'}`}
                          style={{ width: `${(count / Math.max(data?.total_subscribed || 1, 1)) * 100}%` }}
                        />
                      </div>
                      <span className="font-mono text-lg font-bold w-8 text-right">{count}</span>
                    </div>
                  </div>
                ))}
                
                {/* Free & Trial */}
                <div className="pt-4 border-t border-border space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-full bg-accent/20">
                        <Clock className="h-5 w-5 text-accent" />
                      </div>
                      <span className="font-bold">Active Trials</span>
                    </div>
                    <span className="font-mono text-lg font-bold text-accent">{data?.trial_users || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-full bg-muted">
                        <User className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <span className="font-bold">Free Users (No Sub)</span>
                    </div>
                    <span className="font-mono text-lg font-bold text-muted-foreground">{data?.free_users || 0}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recent Subscriptions */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                Recent Subscriptions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {data?.recent_subscriptions?.map((sub, i) => (
                  <motion.div
                    key={sub.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                        <span className="font-bold text-primary">
                          {sub.name?.[0]?.toUpperCase() || '?'}
                        </span>
                      </div>
                      <div>
                        <p className="font-bold text-sm">{sub.name}</p>
                        <p className="text-xs text-muted-foreground">{sub.email}</p>
                      </div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-bold uppercase border ${TIER_COLORS[sub.subscription_tier]}`}>
                      {sub.subscription_tier}
                    </div>
                  </motion.div>
                ))}
                
                {(!data?.recent_subscriptions || data.recent_subscriptions.length === 0) && (
                  <p className="text-center text-muted-foreground py-8">No recent subscriptions</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
